import { useEffect, lazy, Suspense } from "react";
import Lenis from "lenis";
import "@/App.css";
import { StudioProvider, useStudio } from "@/context/StudioContext";
import { Toaster } from "@/components/ui/sonner";
import { HeaderNav } from "@/components/HeaderNav";
import { HeroSection } from "@/components/HeroSection";
import { ShowreelVideoBar } from "@/components/ShowreelVideoBar";

const EditorialMarquee = lazy(() =>
    import("@/components/EditorialMarquee").then((m) => ({ default: m.EditorialMarquee }))
);
const PortfolioGrid = lazy(() =>
    import("@/components/PortfolioGrid").then((m) => ({ default: m.PortfolioGrid }))
);
const AboutStudio = lazy(() =>
    import("@/components/AboutStudio").then((m) => ({ default: m.AboutStudio }))
);
const ServicesGrid = lazy(() =>
    import("@/components/ServicesGrid").then((m) => ({ default: m.ServicesGrid }))
);
const ContactSection = lazy(() =>
    import("@/components/ContactSection").then((m) => ({ default: m.ContactSection }))
);
const FooterSection = lazy(() =>
    import("@/components/FooterSection").then((m) => ({ default: m.FooterSection }))
);
const PrivacyModal = lazy(() =>
    import("@/components/PrivacyModal").then((m) => ({ default: m.PrivacyModal }))
);
const WhatsAppFloat = lazy(() =>
    import("@/components/WhatsAppFloat").then((m) => ({ default: m.WhatsAppFloat }))
);
const AmbientAudio = lazy(() =>
    import("@/components/AmbientAudio").then((m) => ({ default: m.AmbientAudio }))
);

const SiteShell = () => {
    const { lenisRef, theme } = useStudio();

    useEffect(() => {
        const lenis = new Lenis({
            duration: 1.15,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
        });
        lenisRef.current = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, [lenisRef]);

    return (
        <div id="inicio" className="min-h-screen bg-background text-foreground" data-testid="valrio-site">
            <HeaderNav />
            <main>
                <ShowreelVideoBar />
                <HeroSection />
                <Suspense fallback={null}>
                    <EditorialMarquee />
                    <PortfolioGrid />
                    <AboutStudio />
                    <ServicesGrid />
                    <ContactSection />
                </Suspense>
            </main>
            <Suspense fallback={null}>
                <FooterSection />
                <PrivacyModal />
                <WhatsAppFloat />
                <AmbientAudio />
            </Suspense>
            <Toaster position="bottom-right" theme={theme} closeButton />
        </div>
    );
};

export default function App() {
    return (
        <StudioProvider>
            <SiteShell />
        </StudioProvider>
    );
}
